import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Box, Button, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  type DragEndEvent,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import GameScreenShell from "@components/Home/Games/GameScreenShell";
import SessionSummary, { SessionSummaryStats } from "@components/Home/Games/SessionSummary";
import type { GameSessionGame } from "@models/game";
import { GAME_IDS } from "@models/game";
import {
  useCreateSessionMutation,
  useSubmitRoundMutation,
  useCompleteSessionMutation,
} from "@store/gameApi";
import { RootState } from "@store/store";
import type { RuleSwitchObject, RuleAttribute } from "./ruleSwitchContent";
import {
  getLevelParams,
  resolveRoundContent,
  getRuleAtIndex,
  getBinValueSlugsForRule,
  getCorrectBinIndex,
  computeRuleSwitchRoundMetrics,
  type RoundContent,
  type RuleSwitchLevelParams,
  type RuleSwitchSortRecord,
} from "./ruleSwitchContent";
import {
  gameContinueButtonSx,
  gamePrimaryButtonSx,
} from "@components/Home/Games/gameUi";
import { getRuleSwitchAttributeLabel } from "@utils/contentBankTranslations";

export interface RuleSwitchGuidedProps {
  guidedSessionId?: string;
  guidedCurrentGame?: GameSessionGame;
  guidedGameIndex?: number;
  guidedTotalGames?: number;
  onGuidedGameComplete?: () => void;
}

const ROUNDS_PER_SESSION = 3;
const SESSION_START_DEDUPE_MS = 2000;
const BIN_MIN_HEIGHT_XS = 92;
const BIN_MIN_HEIGHT_SM = 104;
let lastSessionStartAt = 0;

type Phase = "idle" | "playing" | "feedback";

interface FreePlayLocationState {
  freePlaySessionId?: string;
  freePlayGames?: GameSessionGame[];
}

type SortRecord = RuleSwitchSortRecord;

function DraggableObject({
  obj,
  disabled,
  isWrongShake,
  disableDragListeners,
  isTapArmed,
  onTap,
  frameSizePx,
  shapeLargePx,
  shapeSmallPx,
}: {
  obj: RuleSwitchObject;
  disabled: boolean;
  isWrongShake: boolean;
  disableDragListeners: boolean;
  isTapArmed: boolean;
  onTap: () => void;
  frameSizePx: number;
  shapeLargePx: number;
  shapeSmallPx: number;
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: obj.id,
    disabled,
  });
  const style = transform
    ? isDragging
      ? { transform: CSS.Translate.toString(transform) }
      : undefined
    : undefined;
  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...(disableDragListeners ? {} : listeners)}
      {...(disableDragListeners ? {} : attributes)}
      sx={{
        width: frameSizePx,
        height: frameSizePx,
        flexShrink: 0,
        cursor: disabled ? "default" : disableDragListeners ? "pointer" : "grab",
        opacity: isDragging ? 0.2 : 1,
        outline: isTapArmed ? "4px solid" : "none",
        outlineColor: isTapArmed ? "primary.main" : undefined,
        outlineOffset: 4,
        animation: isWrongShake ? "ruleSwitchShake 0.4s ease" : undefined,
        "@keyframes ruleSwitchShake": {
          "0%, 100%": { transform: "translateX(0)" },
          "25%": { transform: "translateX(-6px)" },
          "75%": { transform: "translateX(6px)" },
        },
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) onTap();
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onTap();
        }
      }}
    >
      <RuleObjectShape
        obj={obj}
        largePx={shapeLargePx}
        smallPx={shapeSmallPx}
      />
    </Box>
  );
}

const SHAPE_COLOR_MAP: Record<string, string> = {
  red: "#c62828",
  blue: "#1565c0",
  green: "#2e7d32",
  yellow: "#f9a825",
  purple: "#6a1b9a",
  orange: "#ef6c00",
};

function RuleObjectShape({
  obj,
  largePx,
  smallPx,
}: {
  obj: RuleSwitchObject;
  largePx: number;
  smallPx: number;
}) {
  const size = obj.size === "large" ? largePx : smallPx;
  const bgColor = SHAPE_COLOR_MAP[obj.color] ?? obj.color;
  const baseSx = {
    width: size,
    height: size,
    backgroundColor: bgColor,
    border: "3px solid",
    borderColor: "#333",
    borderRadius: obj.shape === "circle" ? "50%" : 0,
    clipPath:
      obj.shape === "triangle"
        ? "polygon(50% 0%, 100% 100%, 0% 100%)"
        : obj.shape === "star"
          ? "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)"
          : obj.shape === "diamond"
            ? "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)"
            : obj.shape === "hexagon"
              ? "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)"
              : undefined,
    backgroundImage:
      obj.pattern === "striped"
        ? `repeating-linear-gradient(
          45deg,
          transparent,
          transparent 4px,
          rgba(0,0,0,0.15) 4px,
          rgba(0,0,0,0.15) 8px
        )`
        : obj.pattern === "dotted"
          ? "radial-gradient(circle at 20% 30%, rgba(0,0,0,0.3) 2px, transparent 2px), radial-gradient(circle at 60% 70%, rgba(0,0,0,0.3) 2px, transparent 2px)"
          : undefined,
  };
  return <Box sx={baseSx} />;
}

function DroppableBin({
  binIndex,
  label,
  isOver,
  isTapArmed,
  onTap,
  disabled,
}: {
  binIndex: number;
  label: string;
  isOver: boolean;
  isTapArmed: boolean;
  onTap: (binIndex: number) => void;
  disabled: boolean;
}) {
  const { setNodeRef, isOver: over } = useDroppable({ id: `bin-${binIndex}` });
  const active = isOver || over || isTapArmed;
  return (
    <Box
      ref={setNodeRef}
      sx={{
        flex: "1 1 0",
        minWidth: 0,
        width: "100%",
        maxWidth: "100%",
        minHeight: { xs: BIN_MIN_HEIGHT_XS, sm: BIN_MIN_HEIGHT_SM },
        border: "3px solid",
        borderColor: active ? "primary.main" : "grey.400",
        borderRadius: 2,
        bgcolor: active ? "action.hover" : "grey.100",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: { xs: 0.75, sm: 1 },
        boxSizing: "border-box",
        cursor: disabled ? "default" : "pointer",
        transition: "border-color 0.2s, background-color 0.2s",
      }}
      role="button"
      tabIndex={disabled ? -1 : 0}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (disabled) return;
        onTap(binIndex);
      }}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onTap(binIndex);
        }
      }}
    >
      <Typography
        variant="body2"
        fontWeight={600}
        textAlign="center"
        sx={{
          fontSize: "0.98rem",
          lineHeight: 1.1,
          whiteSpace: "pre-line",
        }}
      >
        {label}
      </Typography>
    </Box>
  );
}

const RuleSwitch: FC<RuleSwitchGuidedProps> = ({
  guidedSessionId,
  guidedCurrentGame,
  guidedGameIndex = 0,
  guidedTotalGames = 1,
  onGuidedGameComplete,
}) => {
  const theme = useTheme();
  const isTouchDevice = useMediaQuery("(hover: none) and (pointer: coarse)");
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const isSmUp = useMediaQuery(theme.breakpoints.up("sm"));
  const frameSizePx = isMdUp ? 120 : isSmUp ? 100 : 88;
  const shapeLargePx = isMdUp ? 96 : isSmUp ? 80 : 68;
  const shapeSmallPx = isMdUp ? 64 : isSmUp ? 54 : 46;
  const location = useLocation();
  const freePlayState = location.state as FreePlayLocationState | null;
  const freePlaySessionId = freePlayState?.freePlaySessionId;
  const freePlayGame = freePlayState?.freePlayGames?.[0];

  const isGuided = Boolean(guidedSessionId && guidedCurrentGame);
  const isFreePlayFromLibrary = Boolean(freePlaySessionId && freePlayGame);
  const effectiveSessionId = guidedSessionId ?? freePlaySessionId ?? null;
  const effectiveGame = guidedCurrentGame ?? freePlayGame;

  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const selectedPatientId = useSelector(
    (state: RootState) => state.home.selectedPatientId
  );
  const sessionStartedRef = useRef(false);
  const [createSession] = useCreateSessionMutation();
  const [sessionStartError, setSessionStartError] = useState<string | null>(null);
  const [submitRound] = useSubmitRoundMutation();
  const [completeSession] = useCompleteSessionMutation();
  const contentSeed = effectiveGame?.contentSeed ?? "";
  const difficulty = effectiveGame
    ? Math.max(1, Math.min(10, effectiveGame.difficulty))
    : 1;

  const [sessionId, setSessionId] = useState<string | null>(effectiveSessionId);
  const [phase, setPhase] = useState<Phase>("idle");
  const [roundCount, setRoundCount] = useState(0);
  const [roundContent, setRoundContent] = useState<RoundContent | null>(null);
  const [currentObjectIndex, setCurrentObjectIndex] = useState(0);
  const [sortHistory, setSortHistory] = useState<SortRecord[]>([]);
  const [ruleSwitchFlash, setRuleSwitchFlash] = useState(false);
  const [wrongShake, setWrongShake] = useState(false);
  const [roundStartTime, setRoundStartTime] = useState(0);
  const [switchTimings, setSwitchTimings] = useState<number[]>([]);
  const [, setXpEarned] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timerSeconds, setTimerSeconds] = useState(0);
  const [sessionScore, setSessionScore] = useState(0);
  const [roundResults, setRoundResults] = useState<{ accuracy: number; completionMs: number }[]>([]);
  const [domainUpdatesSum, setDomainUpdatesSum] = useState<Record<string, number>>({});
  const [showSessionSummary, setShowSessionSummary] = useState(false);
  const [summaryStats, setSummaryStats] = useState<SessionSummaryStats | null>(null);
  const [muted, setMuted] = useState(false);
  const [tapArmed, setTapArmed] = useState(false);
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const [freePlayGameFromSession, setFreePlayGameFromSession] = useState<GameSessionGame | null>(null);
  const submittedForRoundRef = useRef(false);
  const sessionSeedRef = useRef<string | null>(null);

  const effectiveGameResolved = effectiveGame ?? freePlayGameFromSession;

  const sessionSeed = useMemo(() => {
    if (sessionSeedRef.current) return sessionSeedRef.current;
    sessionSeedRef.current = `session-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
    return sessionSeedRef.current;
  }, []);

  const contentSeedResolved = effectiveGameResolved?.contentSeed ?? contentSeed;
  const difficultyResolved = effectiveGameResolved
    ? Math.max(1, Math.min(10, effectiveGameResolved.difficulty))
    : difficulty;

  const levelParams = useMemo<RuleSwitchLevelParams>(
    () => getLevelParams(difficultyResolved),
    [difficultyResolved]
  );

  const currentRule = useMemo(() => {
    if (!roundContent) return "color";
    return getRuleAtIndex(roundContent.ruleSchedule, currentObjectIndex);
  }, [roundContent, currentObjectIndex]);

  const binLabels = useMemo(() => {
    if (!roundContent) return [];
    const slugBins = getBinValueSlugsForRule(currentRule, roundContent.levelParams.binCount);
    return slugBins.map((slugs) =>
      slugs.map((slug) => getRuleSwitchAttributeLabel(t, currentRule, slug)).join("\n")
    );
  }, [roundContent, currentRule, t, i18n.language]);

  const currentObject = roundContent?.objectSequence[currentObjectIndex] ?? null;
  const roundComplete =
    roundContent && currentObjectIndex >= roundContent.objectSequence.length;

  const startNewRound = useCallback(
    (roundIndex?: number) => {
      submittedForRoundRef.current = false;
      const idx = roundIndex ?? roundCount;
      const content = resolveRoundContent(
        contentSeedResolved,
        idx,
        levelParams,
        undefined,
        sessionSeed
      );
      setRoundContent(content);
      setCurrentObjectIndex(0);
      setSortHistory([]);
      setRuleSwitchFlash(false);
      setWrongShake(false);
      setSwitchTimings([]);
      setRoundStartTime(Date.now());
      setXpEarned(null);
      setTapArmed(false);
      setPhase("playing");
    },
    [contentSeedResolved, roundCount, levelParams, sessionSeed]
  );

  useEffect(() => {
    if (phase !== "playing") setTapArmed(false);
  }, [phase]);

  useEffect(() => {
    if (isGuided || isFreePlayFromLibrary) return;
    if (!selectedPatientId) {
      setSessionStartError(t("games.errors.onlyPatientCanPlay"));
      return;
    }
    if (sessionStartedRef.current) return;
    const now = Date.now();
    if (now - lastSessionStartAt < SESSION_START_DEDUPE_MS) return;
    lastSessionStartAt = now;
    sessionStartedRef.current = true;
    setSessionStartError(null);
    let mounted = true;
    createSession({
      patientId: selectedPatientId,
      sessionType: "freeplay",
      gameId: GAME_IDS.ruleSwitch,
    })
      .unwrap()
      .then((res) => {
        if (mounted && res?.sessionId && res?.games?.[0]) {
          setSessionId(res.sessionId);
          setFreePlayGameFromSession(res.games[0]);
          setSessionStartTime(Date.now());
        }
      })
      .catch((err: { status?: number }) => {
        if (mounted && err?.status === 403) {
          setSessionStartError(t("games.errors.onlyPatientCanPlay"));
        }
      });
    return () => {
      mounted = false;
    };
  }, [isGuided, isFreePlayFromLibrary, selectedPatientId, createSession, t, sessionSeed]);

  useEffect(() => {
    if ((isGuided && guidedSessionId) || isFreePlayFromLibrary) {
      setSessionStartTime(Date.now());
    }
  }, [isGuided, guidedSessionId, isFreePlayFromLibrary]);

  useEffect(() => {
    if (sessionStartTime == null || paused) return;
    const id = setInterval(() => {
      setTimerSeconds(Math.floor((Date.now() - sessionStartTime) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [sessionStartTime, paused]);

  // Rule switch: when currentObjectIndex crosses a schedule boundary, flash
  useEffect(() => {
    if (!roundContent || phase !== "playing") return;
    const schedule = roundContent.ruleSchedule;
    for (let i = 1; i < schedule.length; i++) {
      if (currentObjectIndex === schedule[i].fromIndex) {
        setRuleSwitchFlash(true);
        setSwitchTimings((prev) => [...prev, Date.now()]);
        const t = setTimeout(() => setRuleSwitchFlash(false), 500);
        return () => clearTimeout(t);
      }
    }
  }, [roundContent, currentObjectIndex, phase]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 10 },
    })
  );


  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (!roundContent || phase !== "playing" || currentObjectIndex >= roundContent.objectSequence.length) return;
      if (isTouchDevice) return;
      const { active, over } = event;
      const obj = roundContent.objectSequence[currentObjectIndex];
      if (obj.id !== active.id) return;

      const correctBinIndex = getCorrectBinIndex(
        obj,
        currentRule,
        roundContent.levelParams.binCount
      );
      let droppedBinIndex = correctBinIndex;
      if (over?.id && String(over.id).startsWith("bin-")) {
        droppedBinIndex = parseInt(String(over.id).replace("bin-", ""), 10);
      }

      const correct = droppedBinIndex === correctBinIndex;
      const record: SortRecord = {
        objectIndex: currentObjectIndex,
        binIndex: droppedBinIndex,
        correct,
        timestamp: Date.now(),
        ruleAtSort: currentRule,
      };

      if (!correct) {
        setWrongShake(true);
        const t = setTimeout(() => {
          setWrongShake(false);
          setSortHistory((h) => [...h, record]);
          setCurrentObjectIndex((i) => i + 1);
          setTapArmed(false);
        }, 400);
        return () => clearTimeout(t);
      }

      setSortHistory((h) => [...h, record]);
      setCurrentObjectIndex((i) => i + 1);
      setTapArmed(false);
    },
    [roundContent, phase, currentObjectIndex, currentRule, isTouchDevice]
  );

  const handleObjectTap = useCallback(() => {
    if (!isTouchDevice) return;
    if (!roundContent || phase !== "playing") return;
    if (currentObjectIndex >= roundContent.objectSequence.length) return;
    if (!currentObject) return;
    setTapArmed(true);
  }, [isTouchDevice, roundContent, phase, currentObjectIndex, currentObject]);

  const handleBucketTap = useCallback(
    (binIndex: number) => {
      if (!isTouchDevice) return;
      if (!roundContent || phase !== "playing") return;
      if (currentObjectIndex >= roundContent.objectSequence.length) return;
      if (!currentObject) return;
      if (!tapArmed) return;

      const correctBinIndex = getCorrectBinIndex(
        currentObject,
        currentRule,
        roundContent.levelParams.binCount
      );
      const droppedBinIndex = binIndex;

      const correct = droppedBinIndex === correctBinIndex;
      const record: SortRecord = {
        objectIndex: currentObjectIndex,
        binIndex: droppedBinIndex,
        correct,
        timestamp: Date.now(),
        ruleAtSort: currentRule,
      };

      setTapArmed(false);

      if (!correct) {
        setWrongShake(true);
        const t = setTimeout(() => {
          setWrongShake(false);
          setSortHistory((h) => [...h, record]);
          setCurrentObjectIndex((i) => i + 1);
        }, 400);
        return () => clearTimeout(t);
      }

      setSortHistory((h) => [...h, record]);
      setCurrentObjectIndex((i) => i + 1);
    },
    [
      isTouchDevice,
      roundContent,
      phase,
      currentObjectIndex,
      currentObject,
      tapArmed,
      currentRule,
    ]
  );

  // When round just completed, submit and go to feedback
  useEffect(() => {
    if (
      !roundContent ||
      phase !== "playing" ||
      !roundComplete ||
      submittedForRoundRef.current
    )
      return;
    submittedForRoundRef.current = true;

    const metrics = computeRuleSwitchRoundMetrics(
      roundContent,
      sortHistory,
      roundStartTime,
      switchTimings,
    );
    const { accuracy, completionTimeMs, errorCount } = metrics;

    setRoundResults((prev) => [...prev, { accuracy, completionMs: completionTimeMs }]);

    if (sessionId) {
      submitRound({
        sessionId,
        body: {
          gameId: GAME_IDS.ruleSwitch,
          roundNumber: roundCount + 1,
          difficulty: difficultyResolved,
          accuracy,
          reactionTimeMs: null,
          completionTimeMs,
          errorCount,
          rawData: {
            sortHistory,
            switchTimings: [...switchTimings],
            adaptationTimes: metrics.adaptationTimes,
            switchErrorRate: metrics.switchErrorRate,
            adaptationTime: metrics.adaptationTime,
            persistenceErrors: metrics.persistenceErrors,
          },
        },
      })
        .unwrap()
        .then((res) => {
          if (res?.xpEarned != null) {
            setXpEarned(res.xpEarned);
            setSessionScore((s) => s + res.xpEarned);
          }
          if (res?.domainUpdates && Object.keys(res.domainUpdates).length > 0) {
            setDomainUpdatesSum((prev) => {
              const next = { ...prev };
              for (const [k, v] of Object.entries(res.domainUpdates)) {
                next[k] = (next[k] ?? 0) + v;
              }
              return next;
            });
          }
        })
        .catch(() => {});
    } else {
      const xp = accuracy >= 0.8 ? 20 : 10;
      setXpEarned(xp);
      setSessionScore((s) => s + xp);
    }
    setPhase("feedback");
  }, [
    roundContent,
    phase,
    roundComplete,
    sortHistory,
    roundStartTime,
    switchTimings,
    roundCount,
    difficultyResolved,
    sessionId,
    submitRound,
  ]);

  const handleNextRound = useCallback(() => {
    const nextRound = roundCount + 1;
    if (nextRound >= ROUNDS_PER_SESSION) {
      if (isGuided && onGuidedGameComplete) {
        onGuidedGameComplete();
        return;
      }
      const allResults = [...roundResults];
      const totalRounds = allResults.length;
      const avgAccuracy =
        totalRounds > 0
          ? (allResults.reduce((s, r) => s + r.accuracy, 0) / totalRounds) * 100
          : 0;
      const avgCompletionSec =
        totalRounds > 0
          ? allResults.reduce((s, r) => s + r.completionMs, 0) / totalRounds / 1000
          : 0;

      if (sessionId) {
        completeSession({ sessionId })
          .unwrap()
          .then((res) => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: res?.totalXp ?? sessionScore,
              domainChanges: domainUpdatesSum,
            });
          })
          .catch(() => {
            setSummaryStats({
              accuracyPercent: avgAccuracy,
              avgReactionSeconds: avgCompletionSec,
              xpEarned: sessionScore,
              domainChanges: domainUpdatesSum,
            });
          });
      } else {
        setSummaryStats({
          accuracyPercent: avgAccuracy,
          avgReactionSeconds: avgCompletionSec,
          xpEarned: sessionScore,
          domainChanges: domainUpdatesSum,
        });
      }
      lastSessionStartAt = 0;
      setSessionId(null);
      setShowSessionSummary(true);
      return;
    }
    setRoundCount(nextRound);
    startNewRound(nextRound);
  }, [
    roundCount,
    roundResults,
    sessionId,
    sessionScore,
    domainUpdatesSum,
    isGuided,
    onGuidedGameComplete,
    completeSession,
    startNewRound,
  ]);

  const handleExit = useCallback(() => {
    if (sessionId) completeSession({ sessionId }).catch(() => {});
    lastSessionStartAt = 0;
    navigate(-1);
  }, [sessionId, completeSession, navigate]);

  const handleSummaryContinue = useCallback(() => navigate(-1), [navigate]);

  const progress =
    phase === "idle"
      ? 0
      : (roundCount + (phase === "feedback" ? 1 : 0)) / ROUNDS_PER_SESSION;

  const feedbackText =
    phase === "feedback" ? t("games.ruleSwitch.feedbackGood") : null;

  const ruleLabelKey = `games.ruleSwitch.rules.${currentRule}`;
  const ruleLabel = t(ruleLabelKey);
  const rulePromptTemplate = t("games.ruleSwitch.rulePrompt");
  const rulePromptParts = rulePromptTemplate.split("{{rule}}");

  if (showSessionSummary && summaryStats) {
    return (
      <SessionSummary stats={summaryStats} onContinue={handleSummaryContinue} />
    );
  }

  return (
    <GameScreenShell
      gameName={t("games.ruleSwitch.title")}
      level={difficultyResolved}
      progress={progress}
      feedback={feedbackText}
      timerSeconds={sessionStartTime != null ? timerSeconds : null}
      score={sessionScore > 0 ? sessionScore : null}
      paused={paused}
      onBack={handleExit}
      onPause={() => setPaused((p) => !p)}
      gameIndex={isGuided ? guidedGameIndex : undefined}
      totalGames={isGuided ? guidedTotalGames : undefined}
      muted={muted}
      onMuteChange={setMuted}
    >
      {phase === "idle" && (
        <Box
          textAlign="center"
          py={4}
          flex={1}
          display="flex"
          flexDirection="column"
          justifyContent="center"
        >
          {sessionStartError && (
            <Typography color="error" sx={{ mb: 2 }}>
              {sessionStartError}
            </Typography>
          )}
          <Typography variant="h6" gutterBottom>
            {t("games.ruleSwitch.instructionsTitle")}
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            {t("games.ruleSwitch.instructions")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => startNewRound()}
            sx={gameContinueButtonSx}
          >
            {t("games.ruleSwitch.startRound")}
          </Button>
        </Box>
      )}

      {(phase === "playing" || phase === "feedback") && roundContent && (
        <>
          <Typography
            variant="h5"
            fontWeight={700}
            sx={{
              fontSize: "1.5rem",
              mb: 1.5,
              textAlign: "center",
              textDecoration: ruleSwitchFlash ? "line-through" : undefined,
              opacity: ruleSwitchFlash ? 0.6 : 1,
              transition: "opacity 0.3s",
            }}
          >
            {rulePromptParts.length === 2 ? (
              <>
                {rulePromptParts[0]}
                <Box
                  component="span"
                  sx={{
                    color: "primary.main",
                    fontWeight: 800,
                    textUnderlineOffset: 3,
                    textDecoration: "underline",
                  }}
                >
                  {ruleLabel}
                </Box>
                {rulePromptParts[1]}
              </>
            ) : (
              t("games.ruleSwitch.rulePrompt", { rule: ruleLabel })
            )}
          </Typography>

          <DndContext
            sensors={sensors}
            onDragStart={({ active }) => setActiveDragId(String(active.id))}
            onDragCancel={() => setActiveDragId(null)}
            onDragEnd={(event) => {
              setActiveDragId(null);
              handleDragEnd(event);
            }}
          >
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  width: "100%",
                }}
              >
                {phase === "playing" && currentObject && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "100%",
                    }}
                  >
                    <DraggableObject
                      obj={currentObject}
                      disabled={false}
                      isWrongShake={wrongShake}
                      disableDragListeners={isTouchDevice}
                      isTapArmed={tapArmed}
                      onTap={handleObjectTap}
                      frameSizePx={frameSizePx}
                      shapeLargePx={shapeLargePx}
                      shapeSmallPx={shapeSmallPx}
                    />
                  </Box>
                )}

                {phase === "feedback" && (
                  <Box
                    sx={{
                      flex: 1,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Typography variant="h6" color="text.secondary">
                      {t("games.ruleSwitch.feedbackGood")}
                    </Typography>
                  </Box>
                )}
              </Box>

              <Box
                sx={{
                  flexShrink: 0,
                  display: "flex",
                  width: "100%",
                  maxWidth: "100%",
                  gap: { xs: 0.75, sm: 1 },
                  justifyContent: "stretch",
                  alignItems: "stretch",
                  flexWrap: "nowrap",
                  mt: "auto",
                  mb: 1,
                  pb: 1,
                  px: { xs: 0.25, sm: 0.5 },
                  boxSizing: "border-box",
                  minHeight: { xs: BIN_MIN_HEIGHT_XS, sm: BIN_MIN_HEIGHT_SM },
                }}
              >
                {binLabels.map((label, idx) => (
                  <DroppableBin
                    key={idx}
                    binIndex={idx}
                    label={label}
                    isOver={false}
                    isTapArmed={tapArmed}
                    onTap={handleBucketTap}
                    disabled={!tapArmed || wrongShake}
                  />
                ))}
              </Box>

              <DragOverlay dropAnimation={null}>
                {activeDragId &&
                currentObject &&
                activeDragId === currentObject.id ? (
                  <Box
                    sx={{
                      width: frameSizePx,
                      height: frameSizePx,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "grabbing",
                      filter: (th) =>
                        `drop-shadow(0 10px 18px ${th.palette.mode === "dark" ? "rgba(0,0,0,0.55)" : "rgba(0,0,0,0.22)"})`,
                    }}
                  >
                    <RuleObjectShape
                      obj={currentObject}
                      largePx={shapeLargePx}
                      smallPx={shapeSmallPx}
                    />
                  </Box>
                ) : null}
              </DragOverlay>
            </Box>
          </DndContext>

          {phase === "feedback" && (
            <Box sx={{ mt: 3, display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleNextRound}
                sx={gamePrimaryButtonSx}
              >
                {roundCount + 1 >= ROUNDS_PER_SESSION && !isGuided
                  ? t("games.ruleSwitch.finishSession")
                  : t("games.ruleSwitch.nextRound")}
              </Button>
            </Box>
          )}
        </>
      )}
    </GameScreenShell>
  );
};

export default RuleSwitch;
